import { Plus } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOrgTenants } from "../../../hooks/resources.ts";
import { useUrlState } from "../../../hooks/useUrlState.ts";
import NoSelectionPrompt from "../../../components/NoSelectionPrompt.tsx";
import { api, type ProspectOrderListItem, type ProspectOrderListParams } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import AgentActivity from "../../../components/AgentActivity.tsx";
import CollaboratorStack from "../../../components/CollaboratorStack.tsx";
import FormattedDate from "../../../components/FormattedDate.tsx";
import LiveUpdatesPill from "../../../components/LiveUpdatesPill.tsx";
import FilterBar from "../../../components/filters/FilterBar.tsx";
import SavedViewsMenu from "../../../components/filters/SavedViewsMenu.tsx";
import type { FilterSpec } from "../../../components/filters/specs.ts";
import { useFilters } from "../../../components/filters/useFilters.ts";
import StatusChip from "../../../components/StatusChip.tsx";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import { useOrganization } from "../../../contexts/OrganizationContext.tsx";
import { usePointerInside, useLiveList } from "../../../realtime/useLiveList.ts";
import { formatNumber } from "../../../utils/format.ts";
import OrderFormDialog from "./OrderFormDialog.tsx";
import { ORDER_STATUSES, STATUS_LABELS } from "./orderConstants.ts";
import PageHeader from "../../../components/PageHeader.tsx";

const COLUMN_COUNT = 9;
const orderKey = (o: ProspectOrderListItem) => o.id;

function OrdersPage() {
  const { selectedOrgId, currentOrg } = useOrganization();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);

  // Filters live in the URL so a filtered list survives a reload or a shared link.
  // No param means every status / tenant ("all" was never written).
  const url = useUrlState();
  const statusFilter = url.searchParams.get("status") ?? "";
  const tenantFilter = url.searchParams.get("tenant") ?? "";
  const search = (url.searchParams.get("q") ?? "").trim();
  const hasFilters = !!(statusFilter || tenantFilter || search);

  const { data: tenants = [] } = useOrgTenants(selectedOrgId);

  const specs = useMemo<FilterSpec[]>(
    () => [
      {
        kind: "options",
        key: "status",
        label: "Status",
        param: "status",
        multiple: false,
        pinned: true,
        noneLabel: "All statuses",
        options: ORDER_STATUSES.map((st) => ({ value: st, label: STATUS_LABELS[st] })),
      },
      {
        kind: "options",
        key: "tenant",
        label: "Tenant",
        param: "tenant",
        multiple: false,
        pinned: true,
        noneLabel: "All tenants",
        options: tenants.map((t) => ({ value: t.tenant_id, label: t.tenant_name })),
        searchPlaceholder: "Search tenants",
      },
    ],
    [tenants],
  );
  const filters = useFilters(specs, {
    state: url,
    search: { param: "q", placeholder: "Search orders" },
    pageKey: "orders",
    scopeId: selectedOrgId ?? undefined,
    remember: true,
  });

  const params: ProspectOrderListParams = {
    status: statusFilter ? [statusFilter] : undefined,
    tenant_id: tenantFilter || undefined,
    q: search || undefined,
  };
  const filterKey = JSON.stringify(params);

  const orders = useQuery({
    queryKey: keys.prospectOrder.list(selectedOrgId ?? "", filterKey),
    queryFn: () => api.getProspectOrders(selectedOrgId!, params),
    enabled: !!selectedOrgId,
    meta: { live: true },
  });
  // Pushed live; held still while the mouse is on the table, about to click a row.
  const pointer = usePointerInside();
  const live = useLiveList({
    rows: orders.data?.items,
    getId: orderKey,
    frozen: pointer.inside,
    resetKey: `${selectedOrgId}:${filterKey}`,
  });
  // The unfiltered total, for "12 of 340" while filtered.
  const allOrders = useQuery({
    queryKey: keys.prospectOrder.list(selectedOrgId ?? "", JSON.stringify({ limit: 1 })),
    queryFn: () => api.getProspectOrders(selectedOrgId!, { limit: 1 }),
    enabled: !!selectedOrgId && hasFilters,
    meta: { live: true },
  });

  if (!selectedOrgId) {
    return (
      <>
        <PageHeader title="Prospect orders" />
        <NoSelectionPrompt kind="org" />
      </>
    );
  }

  const items = live.rows ?? [];
  const orgName = currentOrg?.name ?? "this organization";
  const total = orders.data?.total;
  const allTotal = allOrders.data?.total;
  const count =
    total == null
      ? undefined
      : hasFilters
        ? allTotal != null
          ? `${formatNumber(total)} of ${formatNumber(allTotal)}`
          : formatNumber(total)
        : `${formatNumber(total)} ${total === 1 ? "order" : "orders"}`;

  return (
    <>
      <PageHeader
        title="Prospect orders"
        description={`Prospecting requests placed for ${orgName}'s tenants`}
        actions={
          <Button variant="contained" startIcon={<Plus />} onClick={() => setShowCreate(true)}>
            New order
          </Button>
        }
      />

      <Box {...pointer.handlers}>
        <TableCard
          loading={orders.isLoading}
          notice={<LiveUpdatesPill pendingNew={live.pendingNew} pendingRemoved={live.pendingRemoved} onApply={live.apply} />}
          toolbar={
            <FilterBar
              filters={filters}
              count={count}
              views={
                <SavedViewsMenu
                  filters={filters}
                  context={{ organizationId: currentOrg?.id, organizationName: currentOrg?.name }}
                />
              }
            />
          }
        >
          <ScrollTable>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Assignees</TableCell>
                <TableCell>Deadline</TableCell>
                <TableCell align="right">Collections</TableCell>
                <TableCell align="right">Comments</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((order) => (
                <TableRow
                  key={order.id}
                  hover
                  aria-disabled={live.removedIds.has(order.id) || undefined}
                  sx={{ cursor: "pointer", ...(live.removedIds.has(order.id) && { opacity: 0.5 }) }}
                  onClick={() => navigate(`/organization/orders/${order.id}`)}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {order.title}
                      </Typography>
                      <AgentActivity runs={order.active_agent_runs} />
                    </Box>
                  </TableCell>
                  <TableCell>{order.tenant_name ?? "-"}</TableCell>
                  <TableCell>
                    <StatusChip kind="order" value={order.status} label={STATUS_LABELS[order.status]} />
                  </TableCell>
                  <TableCell>
                    {order.origin ? (
                      <Tooltip title="Created by this workflow from a matched record">
                        <Chip
                          size="small"
                          variant="outlined"
                          label={order.origin.workflow_name ?? "Workflow"}
                          sx={{ maxWidth: 200 }}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Placed
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <CollaboratorStack collaborators={order.assignees} maxVisible={3} size={28} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {order.deadline ? <FormattedDate value={order.deadline} /> : "-"}
                  </TableCell>
                  <TableCell align="right">{formatNumber(order.collection_count)}</TableCell>
                  <TableCell align="right">{formatNumber(order.comment_count)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
                    <FormattedDate value={order.created_at} />
                  </TableCell>
                </TableRow>
              ))}
              {orders.data && items.length === 0 && (
                <EmptyTableRow colSpan={COLUMN_COUNT}>
                  {hasFilters
                    ? "No prospect orders match your filters"
                    : "No prospect orders yet. Create one to place a prospecting request for a tenant."}
                </EmptyTableRow>
              )}
            </TableBody>
          </ScrollTable>
        </TableCard>
      </Box>

      {showCreate && (
        <OrderFormDialog
          onClose={() => setShowCreate(false)}
          orgId={selectedOrgId}
          onSaved={(order) => navigate(`/organization/orders/${order.id}`)}
        />
      )}
    </>
  );
}

export default OrdersPage;
